// src/features/permissions/PermissionsManager.jsx
import { useState, useEffect, useMemo } from 'react';
import {
  Card,
  Form,
  Button,
  Alert,
  Spinner,
  Badge
} from 'react-bootstrap';
import {
  Shield,
  Search,
  Save,
  CheckCircle,
  XCircle,
  RefreshCw,
  Lock,
  Users,
  Package,
  DollarSign,
  BarChart3,
  UsersRound,
  Layers,
  ChevronDown
} from 'lucide-react';
import roleManagementService from '../../services/roleManagementService';
import permissionService from '../../services/permissionService';
import { useAuth } from '../../context';
import { PERMISSIONS } from '../../constants';
import './PermissionsManager.css';

// Feature -> icon map
const FEATURE_ICONS = {
  Inventory: Package,
  Payroll: DollarSign,
  Reports: BarChart3,
  HR: UsersRound
};

const getFeatureIcon = (featureKey) => FEATURE_ICONS[featureKey] || Layers;

function useInjectFonts() {
  useEffect(() => {
    if (document.getElementById('pm-fonts')) return;
    const link = document.createElement('link');
    link.id = 'pm-fonts';
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Manrope:wght@600;700;800&family=Inter:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
  }, []);
}

export default function PermissionsManager() {
  useInjectFonts();

  // ✅ Permission check
  const { hasPermission } = useAuth();
  const canAssign = hasPermission(PERMISSIONS.ROLES.ASSIGN);

  const [roles, setRoles] = useState([]);
  const [selectedRoleId, setSelectedRoleId] = useState('');
  const [permissions, setPermissions] = useState({});
  const [rolePermissions, setRolePermissions] = useState([]);
  const [initialPermissions, setInitialPermissions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [changes, setChanges] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [saveProgress, setSaveProgress] = useState({ current: 0, total: 0 });

  // Load roles on mount
  useEffect(() => {
    if (canAssign) {
      loadRoles();
    }
  }, [canAssign]);

  // Load permissions when role is selected
  useEffect(() => {
    if (selectedRoleId && canAssign) {
      loadPermissionsForRole(selectedRoleId);
    }
  }, [selectedRoleId, canAssign]);

  const loadRoles = async () => {
    setLoading(true);
    setErrorMessage('');
    try {
      const result = await roleManagementService.getRoles();
      if (result.success) {
        setRoles(result.data || []);
        if (result.data && result.data.length > 0) {
          setSelectedRoleId(result.data[0].uuid);
        }
      } else {
        setErrorMessage(result.message || 'Failed to load roles');
      }
    } catch (err) {
      setErrorMessage('Failed to load roles. Please try again.');
    }
    setLoading(false);
  };

  const loadPermissionsForRole = async (roleId) => {
    setLoading(true);
    setChanges([]);
    setSuccessMessage('');
    setErrorMessage('');
    setSaveProgress({ current: 0, total: 0 });

    try {
      const permResult = await permissionService.getPermissionsGrouped();
      if (!permResult.success) {
        setErrorMessage(permResult.message || 'Failed to load permissions');
        setLoading(false);
        return;
      }

      const groupedPermissions = {};
      let total = 0;
      (permResult.data || []).forEach(feature => {
        const featureName = feature.feature?.name || 'Unknown';
        groupedPermissions[featureName] = {
          licensed: feature.is_licensed || false,
          permissions: (feature.permissions || []).map(p => ({
            id: p.uuid,
            code: p.code,
            name: p.name,
            description: p.description
          }))
        };
        total += (feature.permissions || []).length;
      });
      setPermissions(groupedPermissions);

      const rolePermResult = await roleManagementService.getRolePermissions(roleId);
      if (rolePermResult.success) {
        const assigned = (rolePermResult.data?.permissions || []).map(p => p.uuid);
        setRolePermissions(assigned);
        setInitialPermissions(assigned);
      } else {
        setRolePermissions([]);
        setInitialPermissions([]);
      }
    } catch (err) {
      setErrorMessage('Failed to load permissions. Please try again.');
    }
    setLoading(false);
  };

  const handlePermissionToggle = (permissionId) => {
    if (!canAssign) return;

    const isChecked = rolePermissions.includes(permissionId);
    let newPermissions;

    if (isChecked) {
      newPermissions = rolePermissions.filter(id => id !== permissionId);
    } else {
      newPermissions = [...rolePermissions, permissionId];
    }

    setRolePermissions(newPermissions);
    updateChanges(newPermissions);
  };

  const handleSelectAll = (featureKey, featurePermissions) => {
    if (!canAssign) return;

    const featurePermissionIds = featurePermissions.map(p => p.id);
    const allChecked = featurePermissionIds.every(id => rolePermissions.includes(id));

    let newPermissions;
    if (allChecked) {
      newPermissions = rolePermissions.filter(id => !featurePermissionIds.includes(id));
    } else {
      const toAdd = featurePermissionIds.filter(id => !rolePermissions.includes(id));
      newPermissions = [...rolePermissions, ...toAdd];
    }

    setRolePermissions(newPermissions);
    updateChanges(newPermissions);
  };

  const updateChanges = (newPermissions) => {
    const added = newPermissions.filter(id => !initialPermissions.includes(id));
    const removed = initialPermissions.filter(id => !newPermissions.includes(id));
    const allChanges = [...added, ...removed];
    setChanges(allChanges);
  };

  const handleSave = async () => {
    if (!canAssign) return;
    if (!selectedRoleId || changes.length === 0) return;

    setSaving(true);
    setSuccessMessage('');
    setErrorMessage('');
    setSaveProgress({ current: 0, total: changes.length });

    const added = rolePermissions.filter(id => !initialPermissions.includes(id));
    const removed = initialPermissions.filter(id => !rolePermissions.includes(id));
    const totalChanges = added.length + removed.length;
    let completed = 0;
    let hasError = false;

    try {
      // Step 1: Remove permissions
      for (const permId of removed) {
        if (hasError) break;
        setSaveProgress({ current: completed + 1, total: totalChanges });
        const result = await roleManagementService.removePermission(selectedRoleId, permId);
        if (!result.success) {
          setErrorMessage(`Failed to remove permission: ${result.message || 'Unknown error'}`);
          hasError = true;
          break;
        }
        completed++;
      }

      // Step 2: Add permissions
      if (!hasError) {
        for (const permId of added) {
          setSaveProgress({ current: completed + 1, total: totalChanges });
          const result = await roleManagementService.assignPermission(selectedRoleId, permId);
          if (!result.success) {
            setErrorMessage(`Failed to add permission: ${result.message || 'Unknown error'}`);
            hasError = true;
            break;
          }
          completed++;
        }
      }

      if (!hasError) {
        setInitialPermissions([...rolePermissions]);
        setChanges([]);
        setSuccessMessage('Permissions updated successfully');
        setSaveProgress({ current: totalChanges, total: totalChanges });
        setTimeout(() => setSuccessMessage(''), 4000);
        await loadPermissionsForRole(selectedRoleId);
      }
    } catch (err) {
      setErrorMessage('An unexpected error occurred while saving permissions.');
    }

    setSaving(false);
    setSaveProgress({ current: 0, total: 0 });
  };

  const handleRoleChange = (e) => {
    const roleId = e.target.value;
    setSelectedRoleId(roleId);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const filteredPermissions = useMemo(() => {
    if (!searchTerm.trim()) return permissions;

    const filtered = {};
    Object.entries(permissions).forEach(([feature, data]) => {
      const matchedPermissions = data.permissions.filter(p =>
        p.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.code?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      if (matchedPermissions.length > 0) {
        filtered[feature] = {
          ...data,
          permissions: matchedPermissions
        };
      }
    });
    return filtered;
  }, [permissions, searchTerm]);

  const hasChanges = changes.length > 0;
  const selectedRole = roles.find(r => r.uuid === selectedRoleId);

  const getFeatureCheckState = (featurePermissions) => {
    const ids = featurePermissions.map(p => p.id);
    const checked = ids.filter(id => rolePermissions.includes(id));
    if (checked.length === 0) return 'none';
    if (checked.length === ids.length) return 'all';
    return 'partial';
  };

  const totalPermissions = Object.values(permissions).reduce(
    (sum, feature) => sum + (feature.permissions?.length || 0), 0
  );

  const assignedCount = rolePermissions.length;
  const coveragePercent = totalPermissions > 0
    ? Math.round((assignedCount / totalPermissions) * 100)
    : 0;

  const RING_RADIUS = 42;
  const RING_CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;
  const ringOffset = RING_CIRCUMFERENCE - (coveragePercent / 100) * RING_CIRCUMFERENCE;

  const addedCount = rolePermissions.filter(id => !initialPermissions.includes(id)).length;
  const removedCount = initialPermissions.filter(id => !rolePermissions.includes(id)).length;

  // ✅ Safety guard - access denied if no permission
  if (!canAssign) {
    return (
      <div className="pm-page">
        <div className="pm-shell">
          <div className="pm-access-denied">
            <div className="pm-access-denied-icon">
              <Lock size={48} />
            </div>
            <h2 className="pm-access-denied-title">Access Denied</h2>
            <p className="pm-access-denied-message">
              You don't have permission to manage role permissions.
              Contact your administrator if you believe this is a mistake.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="pm-page">
      <div className="pm-shell">
        {/* Header */}
        <div className="pm-header">
          <div className="pm-header-left">
            <div className="pm-header-icon">
              <Shield size={22} color="white" strokeWidth={2.25} />
            </div>
            <div>
              <span className="pm-eyebrow">Access Control</span>
              <h1 className="pm-title">Manage Permissions</h1>
              <p className="pm-subtitle">Select a role and configure what it can access</p>
            </div>
          </div>
        </div>

        {/* Alerts */}
        {successMessage && (
          <Alert variant="success" className="pm-alert pm-alert-success">
            <CheckCircle size={18} />
            {successMessage}
          </Alert>
        )}

        {errorMessage && (
          <Alert variant="danger" className="pm-alert pm-alert-error">
            <XCircle size={18} />
            {errorMessage}
          </Alert>
        )}

        {/* Layout */}
        <div className="pm-layout">
          {/* MAIN CONTENT - LEFT SIDE */}
          <div className="pm-content">
            <Card className="pm-panel pm-main-panel">
              <Card.Body className="pm-panel-body">
                {/* Toolbar */}
                <div className="pm-toolbar">
                  <div className="pm-search">
                    <Search size={17} className="pm-search-icon" />
                    <input
                      type="text"
                      placeholder="Search permissions by name or code..."
                      value={searchTerm}
                      onChange={handleSearch}
                      className="pm-search-input"
                      disabled={loading}
                    />
                  </div>
                  {hasChanges && (
                    <span className="pm-pending-badge">
                      {changes.length} change{changes.length !== 1 ? 's' : ''} pending
                    </span>
                  )}
                </div>

                {/* Permissions List */}
                {loading ? (
                  <div className="pm-state">
                    <Spinner animation="border" className="pm-state-spinner" />
                    <p className="pm-state-title">Loading permissions...</p>
                  </div>
                ) : Object.keys(filteredPermissions).length === 0 ? (
                  <div className="pm-state">
                    {searchTerm ? (
                      <>
                        <Search size={40} className="pm-state-icon" />
                        <p className="pm-state-title">No permissions match your search</p>
                        <p className="pm-state-subtitle">Try adjusting your search terms</p>
                      </>
                    ) : (
                      <>
                        <Shield size={40} className="pm-state-icon" />
                        <p className="pm-state-title">No permissions available</p>
                        <p className="pm-state-subtitle">No permissions found for this company</p>
                      </>
                    )}
                  </div>
                ) : (
                  <div className="pm-feature-list">
                    {Object.entries(filteredPermissions).map(([featureKey, featureData], index) => {
                      const isLicensed = featureData.licensed;
                      const featurePermissions = featureData.permissions || [];
                      const checkState = getFeatureCheckState(featurePermissions);
                      const FeatureIcon = getFeatureIcon(featureKey);

                      return (
                        <div
                          key={featureKey}
                          className={`pm-feature ${!isLicensed ? 'pm-feature-locked' : ''}`}
                          style={{ animationDelay: `${index * 0.05}s` }}
                        >
                          <div className="pm-feature-head">
                            <div className="pm-feature-identity">
                              <div className="pm-feature-icon">
                                <FeatureIcon size={16} strokeWidth={2.25} />
                              </div>
                              <h3 className="pm-feature-name">{featureKey}</h3>
                              {isLicensed ? (
                                <span className="pm-pill-licensed">Licensed</span>
                              ) : (
                                <span className="pm-pill-locked">Not Licensed</span>
                              )}
                            </div>
                            <Form.Check
                              type="checkbox"
                              id={`select-all-${featureKey}`}
                              label="Select all"
                              checked={checkState === 'all'}
                              indeterminate={checkState === 'partial'}
                              onChange={() => handleSelectAll(featureKey, featurePermissions)}
                              disabled={!isLicensed}
                              className="pm-select-all"
                            />
                          </div>

                          <div className="pm-feature-body">
                            {featurePermissions.map((permission) => {
                              const isChecked = rolePermissions.includes(permission.id);
                              return (
                                <label
                                  key={permission.id}
                                  htmlFor={permission.id}
                                  className={`pm-item ${isChecked ? 'pm-item-checked' : ''}`}
                                >
                                  <Form.Check
                                    type="checkbox"
                                    id={permission.id}
                                    checked={isChecked}
                                    onChange={() => handlePermissionToggle(permission.id)}
                                    disabled={!isLicensed}
                                    className="pm-item-checkbox"
                                  />
                                  <span className="pm-item-text">
                                    <span className="pm-item-name">{permission.name}</span>
                                    <span className="pm-item-code">{permission.code}</span>
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Card.Body>
            </Card>
          </div>

          {/* SIDEBAR - RIGHT SIDE */}
          <div className="pm-sidebar">
            <Card className="pm-panel pm-sidebar-panel">
              <Card.Body className="pm-panel-body">
                {/* Role Selector */}
                <div className="pm-field">
                  <label className="pm-field-label">Role</label>
                  <div className="pm-select-wrapper">
                    <Form.Select
                      value={selectedRoleId}
                      onChange={handleRoleChange}
                      className="pm-select"
                      disabled={loading || roles.length === 0}
                    >
                      {roles.map((role) => (
                        <option key={role.uuid} value={role.uuid}>
                          {role.name}
                        </option>
                      ))}
                    </Form.Select>
                    <ChevronDown size={16} className="pm-select-chevron" />
                  </div>
                </div>

                {/* Selected Role Info */}
                {selectedRole && (
                  <div className="pm-role-card">
                    <p className="pm-role-description">{selectedRole.description || 'No description'}</p>
                    <div className="pm-role-users">
                      <Users size={13} />
                      <span>{selectedRole.user_count || 0} user{selectedRole.user_count !== 1 ? 's' : ''} assigned</span>
                    </div>
                  </div>
                )}

                {/* Coverage Ring */}
                <div className="pm-coverage">
                  <div className="pm-coverage-ring">
                    <svg viewBox="0 0 100 100" className="pm-ring-svg">
                      <circle
                        cx="50" cy="50" r={RING_RADIUS}
                        className="pm-ring-track"
                      />
                      <circle
                        cx="50" cy="50" r={RING_RADIUS}
                        className="pm-ring-progress"
                        strokeDasharray={RING_CIRCUMFERENCE}
                        strokeDashoffset={ringOffset}
                      />
                    </svg>
                    <div className="pm-ring-center">
                      <span className="pm-ring-value">{coveragePercent}%</span>
                      <span className="pm-ring-caption">coverage</span>
                    </div>
                  </div>
                  <div className="pm-coverage-stats">
                    <div className="pm-coverage-stat">
                      <span className="pm-coverage-stat-value">{assignedCount}</span>
                      <span className="pm-coverage-stat-label">Assigned</span>
                    </div>
                    <div className="pm-coverage-divider" />
                    <div className="pm-coverage-stat">
                      <span className="pm-coverage-stat-value">{totalPermissions}</span>
                      <span className="pm-coverage-stat-label">Total</span>
                    </div>
                    <div className="pm-coverage-divider" />
                    <div className="pm-coverage-stat">
                      <span className={`pm-coverage-stat-value ${hasChanges ? 'pm-coverage-stat-pending' : ''}`}>
                        {changes.length}
                      </span>
                      <span className="pm-coverage-stat-label">Pending</span>
                    </div>
                  </div>
                </div>

                {/* Save */}
                <div className="pm-save-block">
                  <Button
                    onClick={handleSave}
                    disabled={!hasChanges || saving || loading}
                    className="pm-save-button"
                  >
                    {saving ? (
                      <>
                        <RefreshCw size={16} className="pm-spin" />
                        {saveProgress.total > 0 ? `Saving ${saveProgress.current}/${saveProgress.total}...` : 'Saving...'}
                      </>
                    ) : (
                      <>
                        <Save size={16} />
                        Save Changes
                      </>
                    )}
                  </Button>
                  {hasChanges && !saving && (
                    <div className="pm-save-hint">
                      {addedCount > 0 && `${addedCount} added`}
                      {addedCount > 0 && removedCount > 0 && ' • '}
                      {removedCount > 0 && `${removedCount} removed`}
                      {addedCount === 0 && removedCount === 0 && `${changes.length} change${changes.length !== 1 ? 's' : ''}`}
                    </div>
                  )}
                  {saving && saveProgress.total > 0 && (
                    <div className="pm-save-progress">
                      <div className="pm-save-progress-bar">
                        <div
                          className="pm-save-progress-fill"
                          style={{ width: `${(saveProgress.current / saveProgress.total) * 100}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
              </Card.Body>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}